import {
  type Permission,
  type PrincipalRole,
  type Session,
  effectivePermissionsForRole,
} from "@backchannel/tax-contracts";
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import { type AccessInput, type AccessState, evaluateAccess, hasPermission } from "./permissions";
import { readStoredPreviewRole, writeStoredPreviewRole } from "./role-preview";
import { parsePrincipalRole, permissionsForPreview } from "./roles";

export type SessionValue = {
  session: Session | null;
  permissions: readonly Permission[];
  can: (permission: Permission) => boolean;
  access: (input: Omit<AccessInput, "permissions">) => AccessState;
  realRole: PrincipalRole | null;
  effectiveRole: PrincipalRole | null;
  previewRole: PrincipalRole | null;
  canPreviewRoles: boolean;
  setPreviewRole: (role: PrincipalRole | null) => void;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({
  session,
  children,
}: {
  session: Session | null;
  children: ReactNode;
}) {
  const realRole = parsePrincipalRole(session?.role);
  const canPreviewRoles = realRole === "SUPER_ADMIN";
  const [storedPreview, setStoredPreview] = useState<PrincipalRole | null>(() =>
    typeof window === "undefined" ? null : readStoredPreviewRole(),
  );
  const previewRole = canPreviewRoles ? storedPreview : null;

  const setPreviewRole = useCallback(
    (role: PrincipalRole | null) => {
      if (!canPreviewRoles) return;
      const next = role === "SUPER_ADMIN" ? null : role;
      writeStoredPreviewRole(next);
      setStoredPreview(next);
    },
    [canPreviewRoles],
  );

  const value = useMemo<SessionValue>(() => {
    const held = session?.permissions ?? [];
    const permissions = permissionsForPreview(
      effectivePermissionsForRole(realRole, held),
      previewRole,
    );
    const effectiveRole = previewRole ?? realRole;
    return {
      session,
      permissions,
      can: (permission) => hasPermission(permissions, permission),
      access: (input) => evaluateAccess({ ...input, permissions }),
      realRole,
      effectiveRole,
      previewRole,
      canPreviewRoles,
      setPreviewRole,
    };
  }, [canPreviewRoles, previewRole, realRole, session, setPreviewRole]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}

/**
 * Convenience wrapper for the common "one permission plus optional state
 * reasons" case. Returning the full `AccessState` (not a boolean) is what
 * keeps denied/disabled/unavailable distinguishable at the call site.
 */
export function useAccess(input: Omit<AccessInput, "permissions">): AccessState {
  const { access } = useSession();
  return access(input);
}
